import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { TAGS_TAG_QUERY_PARAM, plantHref } from '@atbash-labs/plantview-links'
import {
  invalidateTagAcquisitionQueries,
  isDbTagPath,
  runTagClassification,
  setTagArchived,
  tagSummaryIdentityKey,
  useActiveBranch,
  useActiveSite,
  useBatchTagSummariesQuery,
  useDeleteTagMutation,
  useGetAuthzMeQuery,
  useGetPlantEntityQuery,
  useGetPlantLiveTagValuesByIdQuery,
  useGetTagScrapeStatusQuery,
  useSetTagsEnabledMutation,
  useDeleteTagsMutation,
  useSwitchBranch,
  useTagClassificationsQuery,
  useTagInventoryFilteredQuery,
  useTagInventoryHealthQuery,
  useTagInventoryMismatchesQuery,
  useTagInventorySearchQuery,
  useTagInventoryTreeQuery,
  type GraphTagVendor,
  type PlantTagIdValue,
  type TagClassificationProgress,
  type TagInventoryFolder,
  type TagInventoryHealthResult,
  type TagInventoryHistorySourceFilter,
  type TagInventoryMismatch,
  type TagInventoryMismatchStatus,
  type TagInventoryTag,
  type TagRecord,
  type DeleteTagsResult,
  type SetTagsEnabledResult,
  type TagContainerScope,
  type TagSourceTypeName,
  type TagSummaryBatchItem,
  stringifyTagValue
} from '@axilon-apps/domains-ontology-ui-state'
import { NotFoundPage } from '@axilon-apps/libs-ui-dashboard'

import { TAG_VENDOR_LABEL } from '../../components/tags/tag-vendor'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog'
import { Button } from '../../components/ui/button'
import { Checkbox } from '../../components/ui/checkbox'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../../components/ui/resizable'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'
import { formatRelativeTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { useDebouncedValue } from '../../lib/use-debounced-value'
import { useDebugMode } from '../../lib/debug-mode'
import { HistorianStatusBar } from './HistorianStatusBar'
import { useChatAppContext } from '../../chat-app-context'
import { targetModeForSourceMode } from './acquisition-source'
import {
  connectivityPillClass,
  liveConnectivityDescription,
  liveConnectivityLabel
} from './connectivity'
import {
  EMPTY_LIVE_READINGS,
  LIVE_FAILURE_GRACE_POLLS,
  livePollIntervalMs,
  liveRowState,
  mergeLiveReadings
} from './live-readings'
import {
  NO_TREND_VERDICT,
  evaluateTagHealth,
  INVENTORY_TREND_WINDOW_LABEL,
  INVENTORY_TREND_WINDOW_PHRASE,
  isTagTrendEligible,
  type HealthStatus,
  type HealthVerdict
} from './tag-health'
import { buildTagsAppContextEntries } from './tags-app-context'
import { canOpenTagsPage } from './tags-page-access'
import {
  TagBundleToolsDrawer,
  tagBundleToolFromParam,
  type TagBundleTool
} from './TagBundleToolsDrawer'
import TagDetailPanel from './TagDetailPanel'
import RegisterTagsDialog, { type RegisterTagsSelection } from './RegisterTagsDialog'
import ChangeSourceDialog from './ChangeSourceDialog'
import {
  TAGS_PAGES_QUERY_PARAM,
  TAGS_SELECTED_QUERY_PARAM,
  TAGS_TRAIL_QUERY_PARAM,
  decodeFolderTrail,
  decodePageTokens,
  encodeFolderTrail,
  encodePageTokens,
  type FolderTrailEntry
} from './folder-trail'
import TagTreeSidebar, { type TagTreeBreadcrumb } from './TagTreeSidebar'
import {
  deriveHistorizationStatus,
  rowNotCollectedReason,
  type HistorizationStatus
} from './historization-status'

const MAX_INVENTORY_HEALTH_BATCH_SIZE = 500

type RowHealthStatus = HealthStatus | 'not_computed' | 'pending' | 'unavailable'

const TREND_PILL: Record<Exclude<RowHealthStatus, 'pending'>, string> = {
  bad: 'bg-st-down-bg text-st-down',
  warn: 'bg-st-warn-bg text-st-warn',
  ok: 'bg-st-run-bg text-st-run',
  unknown: 'bg-bg-sunk text-ink-3',
  unavailable: 'bg-bg-sunk text-ink-3',
  not_computed: 'border border-dashed border-ink-3 bg-transparent text-ink-3'
}

const TREND_LABEL: Record<Exclude<RowHealthStatus, 'pending'>, string> = {
  bad: 'Problem',
  warn: 'Warning',
  ok: 'Normal',
  unknown: 'No trend data',
  unavailable: 'Trend unavailable',
  not_computed: 'Not computed'
}

function inventoryHealthDescription(
  status: Exclude<RowHealthStatus, 'pending'>,
  reason?: TagInventoryHealthResult['reason']
): string {
  if (status === 'unknown' && reason === 'no_recent_readings') {
    return 'No recent historical readings are available for this tag.'
  }
  if (status !== 'not_computed') return TREND_LABEL[status]
  switch (reason) {
    case 'registry_lookup_failed':
      return 'Trend unavailable: the tag registration could not be loaded.'
    case 'health_unavailable':
      return 'Trend unavailable: the evaluator is not configured.'
    case 'evaluation_failed':
      return 'Trend unavailable: this tag could not be evaluated.'
    default:
      return TREND_LABEL.not_computed
  }
}

function HealthChip({
  status,
  reason,
  notCollectedReason,
  tagId
}: {
  status: RowHealthStatus
  reason?: TagInventoryHealthResult['reason']
  /** Set when no history is being collected; the chip then reads "Disabled". */
  notCollectedReason?: string
  tagId: string
}) {
  if (status === 'pending') {
    return (
      <span
        className="inline-flex min-w-[4.5rem] items-center justify-center justify-self-start rounded-full border border-dashed border-ink-3/60 bg-bg-sunk/40 px-2 py-0.5 text-11 text-ink-3"
        data-testid={`tag-health-${tagId}`}
        data-health-state="pending"
        aria-busy="true"
        aria-label="Loading trend status"
        title={`Loading ${INVENTORY_TREND_WINDOW_PHRASE} trend status`}
      >
        <Loader2 className="size-3 animate-spin" aria-hidden />
      </span>
    )
  }
  const label = notCollectedReason
    ? 'Disabled'
    : status === 'unknown' && reason === 'no_recent_readings'
      ? 'No history'
      : status === 'unknown'
        ? TREND_LABEL.unknown
        : TREND_LABEL[status]
  const description = notCollectedReason ?? inventoryHealthDescription(status, reason)
  const pillClass = notCollectedReason
    ? TREND_PILL.unknown
    : status === 'unknown' && reason === 'no_recent_readings'
      ? TREND_PILL.warn
      : TREND_PILL[status]

  return (
    <span
      className={cn(
        'inline-flex items-center justify-self-start rounded-full px-2 py-0.5 text-11 font-semibold',
        pillClass
      )}
      data-testid={`tag-health-${tagId}`}
      title={description}
    >
      {label}
    </span>
  )
}

function formatLiveValue(reading: PlantTagIdValue | undefined): string {
  if (!reading || reading.error) return '—'
  const value = reading.value
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—'
    if (Math.abs(value) >= 1000) return value.toFixed(0)
    if (Math.abs(value) >= 10) return value.toFixed(1)
    return value.toFixed(2)
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'string') return value
  return stringifyTagValue(value)
}

export type TagsPageRow = TagRecord & {
  graphOnly?: boolean
  vendor?: GraphTagVendor
  graphTagPath?: string
  graphOpcEndpoint?: string | null
  graphOpcServer?: string | null
  graphOpcNodeId?: string | null
  graphOpcItemPath?: string | null
  archived?: boolean
  gatewayHistoryEnabled?: boolean
  /** A PI Web API sidecar serves this tag's history. */
  piHistory?: boolean
  /** A Canary Views sidecar serves this tag's history. */
  canaryHistory?: boolean
  registrationVendor?: string
  registrationProtocol?: string
  acquisitionQuarantined?: boolean
  mismatchStatus?: TagInventoryMismatch['mismatchStatus']
}

function rowGraphPath(row: TagsPageRow): string {
  return row.graphTagPath ?? row.tagPath
}

export function rowDisplayText(row: TagsPageRow): string {
  if (
    row.vendor === 'pi' ||
    row.sourceType === 'pi' ||
    row.registrationProtocol?.toLowerCase() === 'pi' ||
    row.vendor === 'canary' ||
    row.sourceType === 'canary' ||
    row.registrationProtocol?.toLowerCase() === 'canary'
  ) {
    return row.displayLabel.trim() || rowGraphPath(row)
  }
  return row.graphTagPath ?? (row.displayLabel || row.tagPath)
}

function rowIdentityKey(row: TagsPageRow): string {
  if (row.mismatchStatus === 'missing_graph') return `registry:${row.id}`
  return rowGraphNodeID(row) ?? `registry:${row.id}`
}

export function toGraphIdentityTag(row: TagsPageRow): TagRecord & { nodeId: string } {
  const graphNodeId = rowGraphNodeID(row)
  if (!graphNodeId) {
    throw new Error('Cannot create graph identity for a registry-only row.')
  }
  return {
    ...row,
    tagPath: rowGraphPath(row),
    nodeId: graphNodeId
  }
}

function inventorySourceType(tag: TagInventoryTag): TagSourceTypeName {
  const protocol = tag.registration?.protocol.toLowerCase() ?? ''
  if (protocol.includes('opc-da') || protocol === 'opcda') return 'opcda'
  if (protocol.includes('opc')) return 'opcua'
  if (protocol.includes('mtconnect')) return 'mtconnect'
  if (protocol === 'pi') return 'pi'
  if (protocol === 'canary') return 'canary'
  if (protocol.includes('database') || protocol === 'db') return 'database'
  if (protocol.includes('ignition')) return 'ignition'

  switch (tag.vendor) {
    case 'db':
      return 'database'
    case 'mtconnect':
      return 'mtconnect'
    case 'pi':
      return 'pi'
    case 'canary':
      return 'canary'
    case 'opc':
    case 'factorysim':
    case 'aveva':
      return 'opcua'
    case 'ignition':
      return 'ignition'
    default:
      return 'unspecified'
  }
}

function inventoryScrapeMode(tag: TagInventoryTag): TagRecord['scrapeMode'] {
  const mode = tag.registration?.scrapeMode
  return mode === 'subscribe' || mode === 'stream' ? mode : 'poll'
}

export function inventoryTagToRow(tag: TagInventoryTag | TagInventoryMismatch): TagsPageRow {
  const registration = tag.registration
  const registryId = registration?.tagId ?? ''
  const mismatchStatus = 'mismatchStatus' in tag ? tag.mismatchStatus : undefined
  return {
    name: registryId ? `tags/${registryId}` : tag.nodeId,
    id: registryId || tag.nodeId,
    nodeId: tag.nodeId,
    tagPath: tag.tagPath,
    provider: tag.provider,
    pathSegments: [],
    sourceUrl: registration?.sourceUrl ?? '',
    sourceType: inventorySourceType(tag),
    scrapeMode: inventoryScrapeMode(tag),
    frequencyMs: registration?.frequencyMs ?? 0,
    opcNodeId: registration?.opcNodeId ?? tag.opcNodeId,
    displayLabel: tag.name,
    state: registration?.enabled === false ? 'disabled' : 'enabled',
    createdAt: '',
    updatedAt: '',
    graphOnly: registration === null,
    vendor: tag.vendor,
    graphTagPath: tag.nodeId && mismatchStatus !== 'missing_graph' ? tag.tagPath : undefined,
    graphOpcEndpoint: tag.opcEndpoint,
    graphOpcServer: tag.opcServer,
    graphOpcNodeId: tag.opcNodeId,
    graphOpcItemPath: tag.opcItemPath,
    archived: tag.archived,
    gatewayHistoryEnabled: tag.gatewayHistoryEnabled,
    piHistory: registration?.piHistory ?? false,
    canaryHistory: registration?.canaryHistory ?? false,
    registrationVendor: registration?.vendor,
    registrationProtocol: registration?.protocol,
    acquisitionQuarantined: registration?.quarantined ?? false,
    mismatchStatus
  }
}

export function rowVendor(row: TagsPageRow): GraphTagVendor {
  if (row.vendor) return row.vendor
  if (row.sourceType === 'database' || isDbTagPath(row.tagPath)) return 'db'
  if (row.sourceType === 'opcua' || row.sourceType === 'opcda') return 'opc'
  if (row.sourceType === 'ignition') return 'ignition'
  if (row.sourceType === 'mtconnect') return 'mtconnect'
  if (row.sourceType === 'pi') return 'pi'
  if (row.sourceType === 'canary') return 'canary'
  return 'unknown'
}

function rowHistorization(row: TagsPageRow): HistorizationStatus {
  return deriveHistorizationStatus({
    graphOnly: row.graphOnly,
    enabled: row.state !== 'disabled',
    acquisitionQuarantined: row.acquisitionQuarantined,
    registrationProtocol: row.registrationProtocol,
    sourceType: row.sourceType,
    piHistory: row.piHistory,
    canaryHistory: row.canaryHistory,
    gatewayHistoryEnabled: row.gatewayHistoryEnabled
  })
}

export function rowHistorySourceLabel(row: TagsPageRow): string | null {
  if (row.graphOnly || row.acquisitionQuarantined) return null
  switch (row.registrationProtocol?.trim().toLowerCase()) {
    case 'webdev':
      return row.scrapeMode === 'poll' ? 'WebDev' : null
    case 'opc':
      return row.scrapeMode === 'subscribe' ? 'OPC' : null
    case 'mtconnect':
      return row.scrapeMode === 'stream' || row.scrapeMode === 'poll' ? 'MTC' : null
    case 'pi':
      return 'PI'
    case 'canary':
      return 'Canary'
    case 'db':
      return row.scrapeMode === 'poll' ? 'DB' : null
  }
  switch (row.sourceType) {
    case 'ignition':
      return row.scrapeMode === 'poll' ? 'WebDev' : null
    case 'opcua':
      return row.scrapeMode === 'subscribe' ? 'OPC' : null
    case 'opcda':
      return null
    case 'mtconnect':
      return 'MTC'
    case 'database':
      return 'DB'
    case 'pi':
      return 'PI'
    case 'canary':
      return 'Canary'
    default:
      return null
  }
}

export function rowHasGraphIdentity(row: TagsPageRow): boolean {
  return row.graphOnly === true || row.graphTagPath !== undefined
}

export function rowGraphNodeID(row: TagsPageRow): string | undefined {
  return rowHasGraphIdentity(row) ? (row.nodeId ?? undefined) : undefined
}

export interface HistoryCollectionActions {
  enableNodeIds: string[]
  disableNodeIds: string[]
}

// Registered rows already collecting history can only be disabled, and rows
// with collection off can only be enabled. A mixed multiselect or select-all
// keeps both actions so the operator chooses the direction.
export function historyCollectionActions(rows: readonly TagsPageRow[]): HistoryCollectionActions {
  const enableNodeIds: string[] = []
  const disableNodeIds: string[] = []
  for (const row of rows) {
    if (row.graphOnly === true) continue
    const nodeId = rowGraphNodeID(row)
    if (!nodeId) continue
    if (row.state === 'disabled') enableNodeIds.push(nodeId)
    else disableNodeIds.push(nodeId)
  }
  return { enableNodeIds, disableNodeIds }
}

interface ChangeSourceEligibility {
  visible: boolean
  enabled: boolean
  reason?: string
}

function changeSourceEligibility(rows: TagsPageRow[]): ChangeSourceEligibility {
  const registeredRows = rows.filter((row) => row.graphOnly !== true)
  if (registeredRows.length === 0) return { visible: false, enabled: false }
  if (registeredRows.length !== rows.length) {
    return {
      visible: true,
      enabled: false,
      reason: 'Every selected tag must be registered before its source can be changed.'
    }
  }
  if (registeredRows.some((row) => !row.id)) {
    return {
      visible: true,
      enabled: false,
      reason: 'Every selected tag must have a registry ID.'
    }
  }
  const sourceUrls = new Set(registeredRows.map((row) => row.sourceUrl.trim()))
  if (sourceUrls.has('')) {
    return {
      visible: true,
      enabled: false,
      reason: 'Every selected tag must have a nonempty source URL.'
    }
  }
  if (sourceUrls.size !== 1) {
    return {
      visible: true,
      enabled: false,
      reason: 'Selected tags span multiple source URLs. Select tags from one source.'
    }
  }
  const sourceModes = new Set(registeredRows.map((row) => row.scrapeMode))
  if (sourceModes.size !== 1) {
    return {
      visible: true,
      enabled: false,
      reason: 'Selected tags span multiple history modes. Select tags using one mode.'
    }
  }
  const sourceMode = registeredRows[0].scrapeMode
  if (targetModeForSourceMode(sourceMode) === null) {
    return {
      visible: true,
      enabled: false,
      reason: 'Change source supports WebDev poll and OPC subscribe registrations.'
    }
  }
  return { visible: true, enabled: true }
}

type TagsTab = 'all' | 'needs_registration' | 'problems' | 'warnings' | 'archived'
type AcquisitionFilter = 'all' | 'enabled' | 'disabled'
type TagsView = 'tree' | 'search' | 'issues'

const TAGS_VIEW_FILTER_LABEL: Record<TagsTab, string> = {
  all: 'All',
  needs_registration: 'Needs registration',
  problems: 'Problems',
  warnings: 'Warnings',
  archived: 'Archived'
}

const ACQUISITION_FILTER_LABEL: Record<AcquisitionFilter, string> = {
  all: 'All',
  enabled: 'Enabled',
  disabled: 'Disabled'
}

const HISTORY_SOURCE_FILTER_LABEL: Record<TagInventoryHistorySourceFilter, string> = {
  all: 'All',
  webdev: 'WebDev',
  opc: 'OPC'
}

interface InventoryScope {
  kind: 'providers' | 'provider' | 'folder' | 'udt'
  id: string
  label: string
  provider?: string
  folder?: string
  udt?: string
}

interface InventoryCrumb extends TagTreeBreadcrumb {
  scope: InventoryScope
}

function scopeFromInventoryContainer(container: {
  id: string
  name: string
  kind: TagInventoryFolder['kind']
  providerName?: string
}): InventoryScope {
  if (container.kind === 'provider') {
    return {
      kind: 'provider',
      id: container.id,
      label: container.name,
      provider: container.providerName || container.name
    }
  }
  if (container.kind === 'udt') {
    return {
      kind: 'udt',
      id: container.id,
      label: container.name,
      udt: container.id
    }
  }
  return {
    kind: 'folder',
    id: container.id,
    label: container.name,
    folder: container.id
  }
}

const ROOT_SCOPE: InventoryScope = {
  kind: 'providers',
  id: 'providers',
  label: 'Providers'
}

interface AllTagsSelection {
  containerScope?: TagContainerScope
  searchQuery?: string
  summary: string
}

function unavailableHealth(result: TagInventoryHealthResult | undefined): boolean {
  return result?.status === 'not_computed'
}

function TagsPage() {
  const activeBranch = useActiveBranch()
  const activeSite = useActiveSite()
  const { data: authz, isLoading, isFetching } = useGetAuthzMeQuery()
  const debugEnabled = useDebugMode()
  const [searchParams, setSearchParams] = useSearchParams()
  const [navigationContext, setNavigationContext] = useState({ activeBranch, activeSite })
  const contextChanged =
    navigationContext.activeBranch !== activeBranch || navigationContext.activeSite !== activeSite

  useEffect(() => {
    if (!contextChanged) return
    const params = new URLSearchParams(searchParams)
    params.delete(TAGS_TRAIL_QUERY_PARAM)
    params.delete(TAGS_PAGES_QUERY_PARAM)
    params.delete(TAGS_SELECTED_QUERY_PARAM)
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true })
      return
    }
    setNavigationContext({ activeBranch, activeSite })
  }, [activeBranch, activeSite, contextChanged, searchParams, setSearchParams])

  if (!authz && (isLoading || isFetching)) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-3">
        <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
        Loading Tags…
      </div>
    )
  }
  if (!authz || !canOpenTagsPage(authz)) {
    // Tag links elsewhere in the app point here; send non-admins to the tag's entity page.
    const linkedTagId = searchParams.get(TAGS_TAG_QUERY_PARAM)?.trim()
    return linkedTagId ? (
      <Navigate replace to={plantHref(linkedTagId)} />
    ) : (
      <NotFoundPage href="/plant" />
    )
  }

  // Wait for the URL reset before mounting inventory queries in the new context.
  // An initial mount keeps saved navigation so refresh and shared links still work.
  if (contextChanged) return null

  return (
    <TagsPageInventory
      key={`${activeBranch ?? 'default'}:${activeSite}`}
      activeBranch={activeBranch}
      activeSite={activeSite}
      permissions={authz.permissions}
      debugEnabled={debugEnabled}
    />
  )
}

function TagsPageInventory({
  activeBranch,
  activeSite,
  permissions,
  debugEnabled
}: {
  activeBranch: string | undefined
  activeSite: string
  permissions: readonly string[]
  debugEnabled: boolean
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeBundleTool = tagBundleToolFromParam(searchParams.get('tools'))
  const linkedTagNodeId = searchParams.get(TAGS_TAG_QUERY_PARAM)?.trim() ?? ''
  const linkedTagQuery = useGetPlantEntityQuery(linkedTagNodeId, {
    skip: linkedTagNodeId === ''
  })
  const linkedTagSearchText = useMemo(() => {
    const entity = linkedTagQuery.data?.entity
    if (!entity || entity.id !== linkedTagNodeId) return ''
    for (const key of ['tag_path', 'name'] as const) {
      const value = entity[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
    return ''
  }, [linkedTagNodeId, linkedTagQuery.data])
  const initializedLinkedTagIdRef = useRef<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<TagRecord | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [view, setView] = useState<TagsView>('tree')
  const [searchText, setSearchText] = useState('')
  const debouncedSearch = useDebouncedValue(searchText, 250)
  const normalizedSearch = searchText.trim()
  const normalizedDebouncedSearch = debouncedSearch.trim()
  const searchIsSettled = normalizedSearch === normalizedDebouncedSearch
  // The folder trail lives in the URL so a refresh or shared link keeps the open folder.
  const trailParam = searchParams.get(TAGS_TRAIL_QUERY_PARAM)
  const trail = useMemo(() => decodeFolderTrail(trailParam), [trailParam])
  const crumbs = useMemo<InventoryCrumb[]>(
    () => [
      { id: ROOT_SCOPE.id, label: ROOT_SCOPE.label, scope: ROOT_SCOPE },
      ...trail.map((entry) => {
        const scope = scopeFromInventoryContainer({
          id: entry.id,
          name: entry.label,
          kind: entry.kind
        })
        return { id: scope.id, label: scope.label, scope }
      })
    ],
    [trail]
  )
  // Several state changes can land in one tick (open folder + reset pages + clear
  // selection), so each commit builds on the previous one instead of a stale render.
  const paramsRef = useRef(searchParams)
  paramsRef.current = searchParams
  const commitParams = useCallback(
    (mutate: (params: URLSearchParams) => void, options?: { replace?: boolean }) => {
      const params = new URLSearchParams(paramsRef.current)
      mutate(params)
      if (params.toString() === paramsRef.current.toString()) return
      paramsRef.current = params
      setSearchParams(params, options)
    },
    [setSearchParams]
  )
  const pagesParam = searchParams.get(TAGS_PAGES_QUERY_PARAM)
  const pageTokens = useMemo(() => ['', ...decodePageTokens(pagesParam)], [pagesParam])
  const setPageTokens = useCallback(
    (next: string[] | ((previous: string[]) => string[])) => {
      commitParams((params) => {
        const previous = ['', ...decodePageTokens(params.get(TAGS_PAGES_QUERY_PARAM))]
        const resolved = typeof next === 'function' ? next(previous) : next
        const encoded = encodePageTokens(resolved.slice(1))
        if (encoded) params.set(TAGS_PAGES_QUERY_PARAM, encoded)
        else params.delete(TAGS_PAGES_QUERY_PARAM)
      })
    },
    [commitParams]
  )
  const selectedTagKey = searchParams.get(TAGS_SELECTED_QUERY_PARAM)
  const setSelectedTagKey = useCallback(
    (key: string | null) => {
      commitParams(
        (params) => {
          if (key) params.set(TAGS_SELECTED_QUERY_PARAM, key)
          else params.delete(TAGS_SELECTED_QUERY_PARAM)
        },
        { replace: true }
      )
    },
    [commitParams]
  )
  const [searchPagination, setSearchPagination] = useState<{
    query: string
    tokens: string[]
  }>({
    query: '',
    tokens: ['']
  })
  const [issuesPageTokens, setIssuesPageTokens] = useState<string[]>([''])
  const [issuesStatus, setIssuesStatus] = useState<TagInventoryMismatchStatus>('all')
  const [issuesTotalSize, setIssuesTotalSize] = useState<number | null>(null)
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())
  const [allTagsSelection, setAllTagsSelection] = useState<AllTagsSelection | null>(null)
  const [pendingFolderSelection, setPendingFolderSelection] = useState<{
    scopeKey: string
    selection: AllTagsSelection
  } | null>(null)
  const [registerSelection, setRegisterSelection] = useState<RegisterTagsSelection | null>(null)
  const [changeSourceTags, setChangeSourceTags] = useState<TagRecord[]>([])
  const [pendingBulkDelete, setPendingBulkDelete] = useState<DeleteTagsResult | null>(null)
  const [pendingBulkCollection, setPendingBulkCollection] = useState<{
    enabled: boolean
    preview: SetTagsEnabledResult
  } | null>(null)
  const [activeTab, setActiveTab] = useState<TagsTab>('all')
  const [acquisitionFilter, setAcquisitionFilter] = useState<AcquisitionFilter>('all')
  const [historySourceFilter, setHistorySourceFilter] =
    useState<TagInventoryHistorySourceFilter>('all')
  const listRef = useRef<HTMLDivElement | null>(null)
  const selectionAnchorNodeIdRef = useRef<string | null>(null)
  const clearSelectedRows = useCallback(() => {
    selectionAnchorNodeIdRef.current = null
    setSelectedNodeIds(new Set())
    setAllTagsSelection(null)
  }, [])

  const [classifyProgress, setClassifyProgress] = useState<TagClassificationProgress | null>(null)
  const [classifyNotice, setClassifyNotice] = useState<string | null>(null)
  const classifyAbortRef = useRef<AbortController | null>(null)
  useEffect(() => () => classifyAbortRef.current?.abort(), [])

  const canWriteTags = permissions.includes('tag/write')
  const canConfigureHistory = permissions.includes('tag/scrape-config')
  const canManageAcquisition = permissions.includes('acquisition/manage')
  const canClassifyTags = permissions.includes('tag/classify')
  // The LLM classification run is an ingest job, so it also needs ingest/manage.
  const canRunTagClassification = canClassifyTags && permissions.includes('ingest/manage')
  const canWriteGraph = permissions.includes('graph/write')
  const canDeleteTags = canManageAcquisition && canWriteGraph
  const canMaintainBundles = permissions.includes('platform/maintenance')
  const showOrphanCleanup = debugEnabled && canManageAcquisition
  const canUseTools =
    canWriteTags || canRunTagClassification || canMaintainBundles || showOrphanCleanup

  const { switchBranch } = useSwitchBranch()
  const scope = crumbs[crumbs.length - 1].scope
  const pageToken = pageTokens[pageTokens.length - 1]
  const filtersActive =
    acquisitionFilter !== 'all' || historySourceFilter !== 'all' || activeTab !== 'all'
  const usesDefaultTreeInventory = view === 'tree' && !filtersActive
  const treeQuery = useTagInventoryTreeQuery(
    {
      provider: scope.provider,
      folder: scope.folder,
      udt: scope.udt,
      pageToken: usesDefaultTreeInventory && pageToken ? pageToken : undefined,
      pageSize: 500,
      branch: activeBranch,
      site: activeSite
    },
    { skip: view !== 'tree' || !usesDefaultTreeInventory }
  )
  const filteredQuery = useTagInventoryFilteredQuery(
    {
      provider: scope.provider,
      folder: scope.folder,
      udt: scope.udt,
      acquisition: acquisitionFilter,
      historySource: historySourceFilter,
      view: activeTab,
      pageToken: pageToken || undefined,
      branch: activeBranch,
      site: activeSite
    },
    { skip: view !== 'tree' || usesDefaultTreeInventory }
  )
  const tree = usesDefaultTreeInventory ? treeQuery.data : filteredQuery.data
  const searchPageTokens =
    searchPagination.query === normalizedDebouncedSearch ? searchPagination.tokens : ['']
  const searchPageToken = searchPageTokens[searchPageTokens.length - 1]
  const searchQuery = useTagInventorySearchQuery(
    {
      query: normalizedDebouncedSearch,
      pageToken: searchPageToken || undefined,
      pageSize: linkedTagNodeId ? 500 : undefined,
      branch: activeBranch,
      site: activeSite
    },
    {
      skip: view !== 'search' || !searchIsSettled || normalizedDebouncedSearch === ''
    }
  )
  const issuesPageToken = issuesPageTokens[issuesPageTokens.length - 1]
  const issuesQuery = useTagInventoryMismatchesQuery(
    {
      pageToken: issuesPageToken || undefined,
      status: issuesStatus,
      branch: activeBranch,
      site: activeSite
    },
    {
      skip: view !== 'issues'
    }
  )
  // The combined badge comes from the per-direction counts, which every page
  // carries regardless of the status filter, so registering tags while
  // filtered to one direction still refreshes it. Older responses without the
  // counts can only size the badge when the page was unfiltered.
  useEffect(() => {
    const data = issuesQuery.data
    if (view !== 'issues' || !data) return
    const combined =
      typeof data.missingRegistrationCount === 'number' &&
      typeof data.missingGraphCount === 'number'
        ? data.missingRegistrationCount + data.missingGraphCount
        : issuesStatus === 'all'
          ? data.totalSize
          : null
    if (combined !== null) setIssuesTotalSize(combined)
  }, [issuesQuery.data, issuesStatus, view])
  const inventoryTags = useMemo(
    () =>
      view === 'search'
        ? searchIsSettled && normalizedDebouncedSearch !== ''
          ? (searchQuery.data?.results ?? []).map((hit) => hit.tag)
          : []
        : view === 'issues'
          ? (issuesQuery.data?.tags ?? [])
          : usesDefaultTreeInventory
            ? (tree?.tags ?? []).filter((tag) => !tag.archived)
            : (filteredQuery.data?.tags ?? []),
    [
      filteredQuery.data?.tags,
      normalizedDebouncedSearch,
      searchIsSettled,
      searchQuery.data?.results,
      issuesQuery.data?.tags,
      tree?.tags,
      usesDefaultTreeInventory,
      view
    ]
  )
  const tags = useMemo(() => inventoryTags.map(inventoryTagToRow), [inventoryTags])
  const filteredFolderCounts = useMemo(
    () =>
      new Map((filteredQuery.data?.folderCounts ?? []).map((count) => [count.id, count] as const)),
    [filteredQuery.data?.folderCounts]
  )
  const sidebarFolders = useMemo(() => {
    if (!tree) return []
    if (view !== 'tree') return tree.folders
    if (
      filtersActive &&
      (!filteredQuery.data ||
        filteredQuery.isPreviousData ||
        filteredQuery.data.filterStatus !== 'computed')
    ) {
      return []
    }
    return tree.folders.flatMap((folder) => {
      if (!filtersActive) return folder.subtreeTagCount === 0 ? [] : [folder]
      const filteredCount = filteredFolderCounts.get(folder.id)
      if (filteredCount?.matchingTagCount == null || filteredCount.matchingTagCount === 0) return []
      return [
        {
          ...folder,
          subtreeTagCount: filteredCount.matchingTagCount
        }
      ]
    })
  }, [
    filteredFolderCounts,
    filteredQuery.data,
    filteredQuery.isPreviousData,
    filtersActive,
    tree,
    view
  ])
  const sidebarParent = useMemo(() => {
    if (!tree) return undefined
    if (view !== 'tree') return tree.parent
    if (!filtersActive) return tree.parent
    return {
      ...tree.parent,
      subtreeTagCount: filteredQuery.data?.matchingTagCount ?? tree.parent.subtreeTagCount
    }
  }, [filteredQuery.data, filtersActive, tree, view])
  const sidebarCountUnavailableIds = useMemo(() => {
    if (!filtersActive) return new Set<string>()
    return new Set(
      (filteredQuery.data?.folderCounts ?? [])
        .filter((count) => count.matchingTagCount === null)
        .map((count) => count.id)
    )
  }, [filteredQuery.data?.folderCounts, filtersActive])
  const sidebarParentCountUnavailable =
    filtersActive && filteredQuery.data?.matchingTagCount === null

  const healthRequestIds = useMemo(() => {
    if (view === 'issues') return []
    if (view !== 'tree') {
      if (searchQuery.isLoading || searchQuery.isPreviousData) return []
      return tags.map(rowGraphNodeID).filter((id): id is string => id !== undefined)
    }
    if (!tree) return []
    if (
      usesDefaultTreeInventory
        ? treeQuery.isLoading || treeQuery.isPreviousData
        : filteredQuery.isLoading || filteredQuery.isPreviousData
    ) {
      return []
    }
    if (!usesDefaultTreeInventory && !filteredQuery.data) {
      return []
    }
    return [...new Set(inventoryTags.map((tag) => tag.nodeId))].slice(
      0,
      MAX_INVENTORY_HEALTH_BATCH_SIZE
    )
  }, [
    filteredQuery.data,
    filteredQuery.isLoading,
    filteredQuery.isPreviousData,
    inventoryTags,
    searchQuery.isLoading,
    searchQuery.isPreviousData,
    tags,
    tree,
    treeQuery.isLoading,
    treeQuery.isPreviousData,
    usesDefaultTreeInventory,
    view
  ])
  const healthQuery = useTagInventoryHealthQuery(healthRequestIds, {
    branch: activeBranch,
    site: activeSite
  })
  const healthById = useMemo(
    () => new Map((healthQuery.data?.results ?? []).map((result) => [result.id, result])),
    [healthQuery.data]
  )

  const [deleteTag, deleteState] = useDeleteTagMutation()
  const [setTagsEnabled, setTagsEnabledState] = useSetTagsEnabledMutation()
  const [deleteTags, deleteTagsState] = useDeleteTagsMutation()

  const selectedTag = useMemo(
    () => tags.find((tag) => rowIdentityKey(tag) === selectedTagKey),
    [selectedTagKey, tags]
  )
  const selectedGraphNodeId = selectedTag ? rowGraphNodeID(selectedTag) : undefined
  // Bring the selected row into view once it renders: after a refresh, and after the
  // list remounts when the detail panel opens.
  const scrolledKeyRef = useRef<string | null>(null)
  const selectedRowKey = selectedTag ? rowIdentityKey(selectedTag) : null
  useEffect(() => {
    if (!selectedRowKey) {
      scrolledKeyRef.current = null
      return
    }
    if (scrolledKeyRef.current === selectedRowKey) return
    const row = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[data-row-key]') ?? []
    ).find((element) => element.dataset.rowKey === selectedRowKey)
    if (!row) return
    scrolledKeyRef.current = selectedRowKey
    row.scrollIntoView?.({ block: 'center' })
  })
  const selectedStatusQuery = useGetTagScrapeStatusQuery(
    { name: selectedTag?.name ?? '' },
    { skip: !selectedTag || selectedTag.graphOnly === true }
  )

  const activeTags = useMemo(() => tags.filter((tag) => !tag.archived), [tags])
  const visibleTags = tags
  const visibleSelectableNodeIds = useMemo(
    () =>
      visibleTags
        .filter((tag) => view !== 'issues' || tag.mismatchStatus === 'missing_registration')
        .map(rowGraphNodeID)
        .filter((nodeId): nodeId is string => nodeId !== undefined),
    [view, visibleTags]
  )
  const allTagsTarget = useMemo((): AllTagsSelection | null => {
    if (view === 'search') {
      if (!searchIsSettled || normalizedDebouncedSearch === '') return null
      const resultCount = searchQuery.data?.results.length
      const spansPages = searchPageTokens.length > 1 || Boolean(searchQuery.data?.nextPageToken)
      if (searchQuery.data && !spansPages && resultCount === 0) return null
      const quoted = `"${normalizedDebouncedSearch}"`
      return {
        searchQuery: normalizedDebouncedSearch,
        summary:
          spansPages || resultCount === undefined
            ? `All tags matching ${quoted}`
            : `All ${resultCount.toLocaleString()} tags matching ${quoted}`
      }
    }
    if (view !== 'tree' || filtersActive) return null
    if (scope.kind !== 'provider' && scope.kind !== 'folder' && scope.kind !== 'udt') return null
    const subtreeTagCount = tree?.parent.subtreeTagCount
    if (subtreeTagCount === undefined || subtreeTagCount <= 0) return null
    return {
      containerScope: { id: scope.id, kind: scope.kind },
      summary: `All ${subtreeTagCount.toLocaleString()} tags in ${scope.label}`
    }
  }, [
    filtersActive,
    normalizedDebouncedSearch,
    scope.id,
    scope.kind,
    scope.label,
    searchIsSettled,
    searchPageTokens.length,
    searchQuery.data,
    tree?.parent.subtreeTagCount,
    view
  ])
  const nodeIsSelected = (nodeId: string | undefined) =>
    nodeId !== undefined && (allTagsSelection !== null || selectedNodeIds.has(nodeId))
  const allVisibleRowsSelected =
    allTagsSelection !== null ||
    (visibleSelectableNodeIds.length > 0 &&
      visibleSelectableNodeIds.every((nodeId) => selectedNodeIds.has(nodeId)))
  const selectedRows = useMemo(
    () =>
      visibleTags.filter((tag) => {
        const nodeId = rowGraphNodeID(tag)
        return nodeId !== undefined && selectedNodeIds.has(nodeId)
      }),
    [selectedNodeIds, visibleTags]
  )
  const selectedRowNodeIds = useMemo(
    () =>
      selectedRows.map(rowGraphNodeID).filter((nodeId): nodeId is string => nodeId !== undefined),
    [selectedRows]
  )
  const selectedGraphOnlyNodeIds = useMemo(
    () =>
      selectedRows
        .filter((tag) => tag.graphOnly === true)
        .map(rowGraphNodeID)
        .filter((nodeId): nodeId is string => nodeId !== undefined),
    [selectedRows]
  )
  const selectedRegisteredRows = useMemo(
    () => selectedRows.filter((tag) => tag.graphOnly !== true),
    [selectedRows]
  )
  const selectedRegisteredNodeIds = useMemo(
    () =>
      selectedRegisteredRows
        .map(rowGraphNodeID)
        .filter((nodeId): nodeId is string => nodeId !== undefined),
    [selectedRegisteredRows]
  )
  const selectedHistoryActions = useMemo(
    () => historyCollectionActions(selectedRows),
    [selectedRows]
  )
  const selectedChangeSourceEligibility = useMemo(
    () => changeSourceEligibility(selectedRows),
    [selectedRows]
  )

  const visibleGraphNodeIds = useMemo(() => {
    const ids = visibleTags
      .map(rowGraphNodeID)
      .filter((nodeId): nodeId is string => nodeId !== undefined)
    if (selectedGraphNodeId && !ids.includes(selectedGraphNodeId)) ids.push(selectedGraphNodeId)
    return ids
  }, [selectedGraphNodeId, visibleTags])
  const liveReadNodeIds = useMemo(() => {
    const ids = visibleTags
      .filter((tag) => tag.graphOnly !== true)
      .map(rowGraphNodeID)
      .filter((nodeId): nodeId is string => nodeId !== undefined)
    if (
      selectedTag?.graphOnly !== true &&
      selectedGraphNodeId &&
      !ids.includes(selectedGraphNodeId)
    ) {
      ids.push(selectedGraphNodeId)
    }
    return ids
  }, [selectedGraphNodeId, selectedTag?.graphOnly, visibleTags])
  const [liveReadings, setLiveReadings] = useState(EMPTY_LIVE_READINGS)
  const liveValuesQuery = useGetPlantLiveTagValuesByIdQuery(
    { node_ids: liveReadNodeIds },
    {
      pollingInterval:
        liveReadNodeIds.length > 0 ? livePollIntervalMs(liveReadings.failedPolls) : 0,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
      skip: liveReadNodeIds.length === 0
    }
  )
  // RTK Query keeps the previous `data` object when a response is
  // structurally equal, so each poll is counted by its fulfilled timestamp.
  const liveFulfilledAt = liveValuesQuery.fulfilledTimeStamp
  const liveTags = liveValuesQuery.data?.tags
  if (liveTags && liveFulfilledAt !== undefined && liveFulfilledAt !== liveReadings.mergedAt) {
    setLiveReadings(mergeLiveReadings(liveReadings, liveTags, liveFulfilledAt))
  }
  const selectedLive = liveRowState(liveReadings, selectedGraphNodeId)

  const summaryItems = useMemo<TagSummaryBatchItem[]>(() => {
    // Rows take their Trend verdict from the server, so only the selected
    // tag's detail panel needs a summary. Non-historized tags never ask.
    if (!selectedTag?.sourceUrl || !isTagTrendEligible(selectedTag)) return []
    return [
      {
        sourceUrl: selectedTag.sourceUrl,
        sourceType: selectedTag.sourceType,
        tagPath: selectedTag.tagPath,
        tagId: selectedTag.id
      }
    ]
  }, [selectedTag])
  const summariesQuery = useBatchTagSummariesQuery({ items: summaryItems })
  const classificationsQuery = useTagClassificationsQuery(visibleGraphNodeIds)
  const selectedTagHealth = useMemo<HealthVerdict | undefined>(() => {
    if (!selectedTag) return undefined
    if (!isTagTrendEligible(selectedTag)) return NO_TREND_VERDICT
    const nodeId = rowGraphNodeID(selectedTag)
    return evaluateTagHealth(
      nodeId ? classificationsQuery.data?.get(nodeId) : undefined,
      summariesQuery.data.get(tagSummaryIdentityKey(selectedTag.sourceUrl, selectedTag.tagPath)),
      { frequencyMs: selectedTag.frequencyMs }
    )
  }, [classificationsQuery.data, selectedTag, summariesQuery.data])

  const viewFacetCounts = filteredQuery.data?.facets?.view
  const pageHealthUnavailable =
    view === 'tree'
      ? usesDefaultTreeInventory
        ? false
        : filteredQuery.isLoading ||
          Boolean(filteredQuery.error) ||
          viewFacetCounts?.problems === null ||
          viewFacetCounts?.problems === undefined ||
          viewFacetCounts?.warnings === null ||
          viewFacetCounts?.warnings === undefined
      : view === 'issues' ||
        healthQuery.isLoading ||
        Boolean(healthQuery.error) ||
        activeTags.some((tag) => {
          const nodeId = rowGraphNodeID(tag)
          const result = nodeId ? healthById.get(nodeId) : undefined
          return !result || unavailableHealth(result)
        })
  const scopeHasTags = (tree?.parent.subtreeTagCount ?? 0) > 0
  const nextPageToken =
    view === 'search'
      ? searchIsSettled && !searchQuery.isPreviousData
        ? searchQuery.data?.nextPageToken
        : undefined
      : view === 'issues'
        ? issuesQuery.isPreviousData
          ? undefined
          : issuesQuery.data?.nextPageToken
        : usesDefaultTreeInventory
          ? treeQuery.isPreviousData
            ? undefined
            : tree?.nextPageToken
          : filteredQuery.isPreviousData || filteredQuery.data?.filterStatus === 'not_computed'
            ? undefined
            : filteredQuery.data?.nextPageToken
  const currentPageTokens =
    view === 'search' ? searchPageTokens : view === 'issues' ? issuesPageTokens : pageTokens
  const pageHasMore = Boolean(nextPageToken)

  const selectedFolderLabel = crumbs
    .slice(1)
    .map((crumb) => crumb.label)
    .join('/')
  useChatAppContext(
    buildTagsAppContextEntries({
      selectedFolder: selectedFolderLabel,
      sidebarFilter: view === 'search' ? searchText : '',
      healthFilter: activeTab,
      selectedTagPath: selectedTag ? rowGraphPath(selectedTag) : undefined,
      selectedTagId: selectedTag?.id,
      selectedTagHealth: selectedTagHealth?.status,
      visibleTagCount: visibleTags.length,
      pageTagCount: visibleTags.length,
      currentPage: currentPageTokens.length,
      totalPages: currentPageTokens.length + (pageHasMore ? 1 : 0)
    })
  )

  const scopeKey = `${scope.kind}:${scope.id}`
  const activePageToken =
    view === 'search' ? searchPageToken : view === 'issues' ? issuesPageToken : pageToken
  // Skip the first run so a selection restored from the URL survives a refresh.
  const initialPageTokenRef = useRef(activePageToken)
  useEffect(() => {
    if (initialPageTokenRef.current === activePageToken) return
    initialPageTokenRef.current = activePageToken
    clearSelectedRows()
    setSelectedTagKey(null)
    listRef.current?.scrollTo({ top: 0 })
  }, [activePageToken, clearSelectedRows, setSelectedTagKey])

  const initialScopeViewRef = useRef(`${scopeKey}|${view}`)
  useEffect(() => {
    if (initialScopeViewRef.current === `${scopeKey}|${view}`) return
    initialScopeViewRef.current = `${scopeKey}|${view}`
    clearSelectedRows()
    setSelectedTagKey(null)
    listRef.current?.scrollTo({ top: 0 })
  }, [clearSelectedRows, scopeKey, setSelectedTagKey, view])

  // Declared after the scope-change reset above so the folder selection lands
  // once that reset has run for the newly opened folder.
  useEffect(() => {
    if (!pendingFolderSelection || pendingFolderSelection.scopeKey !== scopeKey) return
    setAllTagsSelection(pendingFolderSelection.selection)
    setPendingFolderSelection(null)
  }, [pendingFolderSelection, scopeKey])

  useEffect(() => {
    setSearchPagination({ query: normalizedDebouncedSearch, tokens: [''] })
  }, [normalizedDebouncedSearch])

  useEffect(() => {
    if (!linkedTagNodeId) {
      initializedLinkedTagIdRef.current = null
      return
    }
    if (linkedTagSearchText === '' || initializedLinkedTagIdRef.current === linkedTagNodeId) {
      return
    }
    initializedLinkedTagIdRef.current = linkedTagNodeId
    setSelectedTagKey(null)
    setView('search')
    setSearchText(linkedTagSearchText)
  }, [linkedTagNodeId, linkedTagSearchText])

  useEffect(() => {
    if (!linkedTagNodeId || view !== 'search' || !searchIsSettled) return
    const linkedTag = searchQuery.data?.results.find((hit) => hit.tag.nodeId === linkedTagNodeId)
    if (linkedTag) setSelectedTagKey(linkedTagNodeId)
  }, [linkedTagNodeId, searchIsSettled, searchQuery.data?.results, view])

  useEffect(() => {
    const currentIds = new Set(tags.map(rowGraphNodeID).filter((id): id is string => Boolean(id)))
    setSelectedNodeIds((previous) => {
      const next = new Set([...previous].filter((id) => currentIds.has(id)))
      return next.size === previous.size ? previous : next
    })
  }, [tags])

  const setTrail = useCallback(
    (next: FolderTrailEntry[]) => {
      commitParams((params) => {
        if (next.length > 0) params.set(TAGS_TRAIL_QUERY_PARAM, encodeFolderTrail(next))
        else params.delete(TAGS_TRAIL_QUERY_PARAM)
        params.delete(TAGS_SELECTED_QUERY_PARAM)
        params.delete(TAGS_PAGES_QUERY_PARAM)
      })
    },
    [commitParams]
  )

  const navigateTo = useCallback(
    (folder: TagInventoryFolder) => {
      setView('tree')
      setTrail([...trail, { kind: folder.kind, id: folder.id, label: folder.name }])
      setPageTokens([''])
    },
    [setTrail, trail]
  )

  const selectAllInFolder = useCallback(
    (folder: TagInventoryFolder) => {
      const nextScope = scopeFromInventoryContainer(folder)
      setPendingFolderSelection({
        scopeKey: `${nextScope.kind}:${nextScope.id}`,
        selection: {
          containerScope: { id: folder.id, kind: folder.kind },
          summary: `All ${folder.subtreeTagCount.toLocaleString()} tags in ${folder.name}`
        }
      })
      navigateTo(folder)
    },
    [navigateTo]
  )

  const navigateBreadcrumb = useCallback(
    (index: number) => {
      setView('tree')
      setTrail(trail.slice(0, index))
      setPageTokens([''])
    },
    [setTrail, trail]
  )

  const refetchLevel = useCallback(() => {
    if (view === 'search') searchQuery.refetch()
    else if (view === 'issues') issuesQuery.refetch()
    else if (usesDefaultTreeInventory) treeQuery.refetch()
    else filteredQuery.refetch()
    if (liveReadNodeIds.length > 0) liveValuesQuery.refetch()
  }, [
    filteredQuery,
    liveReadNodeIds.length,
    liveValuesQuery,
    searchQuery,
    treeQuery,
    issuesQuery,
    usesDefaultTreeInventory,
    view
  ])

  const setBundleTool = useCallback(
    (tool: TagBundleTool | null) => {
      commitParams(
        (params) => {
          if (tool) params.set('tools', tool)
          else params.delete('tools')
        },
        { replace: true }
      )
    },
    [commitParams]
  )
  const closeSelectedTag = useCallback(() => {
    commitParams(
      (params) => {
        params.delete(TAGS_SELECTED_QUERY_PARAM)
        params.delete(TAGS_TAG_QUERY_PARAM)
      },
      { replace: true }
    )
  }, [commitParams])
  const handleBundleImported = useCallback(() => {
    invalidateTagAcquisitionQueries()
    refetchLevel()
    healthQuery.refetch()
    summariesQuery.refetch()
    classificationsQuery.refetch()
  }, [classificationsQuery, healthQuery, refetchLevel, summariesQuery])

  const isClassifying = classifyProgress !== null
  const classifiableTags = useMemo(
    () => activeTags.filter((tag) => rowGraphNodeID(tag) !== undefined),
    [activeTags]
  )
  const handleClassify = useCallback(async () => {
    if (!canRunTagClassification || isClassifying || classifiableTags.length === 0) return
    setClassifyNotice(null)
    const controller = new AbortController()
    classifyAbortRef.current = controller
    setClassifyProgress({
      done: 0,
      total: classifiableTags.length,
      classifiedCount: 0,
      skippedCount: 0,
      errorCount: 0
    })
    try {
      const result = await runTagClassification({
        tags: classifiableTags.map(toGraphIdentityTag),
        scope: view === 'tree' ? tree?.parent.id || undefined : undefined,
        signal: controller.signal,
        onProgress: setClassifyProgress
      })
      if (result.state === 'error') {
        setClassifyNotice('Classification failed — see the job on the Ingest page for details.')
      } else if (result.state === 'cancelled') {
        setClassifyNotice(`Classification stopped after ${result.classifiedCount} tag(s).`)
      } else {
        const parts = [`Classified ${result.classifiedCount} tag(s)`]
        if (result.skippedCount > 0) parts.push(`${result.skippedCount} skipped`)
        if (result.errorCount > 0) parts.push(`${result.errorCount} problem(s)`)
        setClassifyNotice(parts.join(' · '))
        if (result.branchId && result.branchId !== activeBranch) {
          await switchBranch(result.branchId).catch(() => {
            setClassifyNotice(
              `Classified ${result.classifiedCount} tag(s) on another branch — switch to it to see the results.`
            )
          })
        }
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setClassifyNotice(
          `Classification failed: ${error instanceof Error ? error.message : String(error)}`
        )
      }
    } finally {
      if (classifyAbortRef.current === controller) classifyAbortRef.current = null
      setClassifyProgress(null)
      classificationsQuery.refetch()
    }
  }, [
    activeBranch,
    canRunTagClassification,
    classifiableTags,
    classificationsQuery,
    isClassifying,
    switchBranch,
    tree?.parent.id,
    view
  ])

  const handleToggleArchive = useCallback(
    async (tag: TagRecord) => {
      if (!canWriteGraph) throw new Error('You do not have permission to archive tags.')
      const row = tags.find((candidate) => candidate.id === tag.id)
      const nodeId = row ? rowGraphNodeID(row) : undefined
      if (!row || !nodeId) throw new Error('This tag has no matching graph identity to archive.')
      await setTagArchived(nodeId, !row.archived)
      if (view === 'issues') issuesQuery.refetch()
      else if (view === 'search') searchQuery.refetch()
      else if (usesDefaultTreeInventory) treeQuery.refetch()
      else filteredQuery.refetch()
    },
    [
      canWriteGraph,
      filteredQuery,
      issuesQuery,
      searchQuery,
      tags,
      treeQuery,
      usesDefaultTreeInventory,
      view
    ]
  )

  const handleConfirmDelete = async () => {
    if (!canWriteTags || !pendingDelete) return
    setActionError(null)
    try {
      await deleteTag({ name: pendingDelete.name })
      setPendingDelete(null)
      setSelectedTagKey(null)
      refetchLevel()
    } catch (error) {
      setActionError(String(error))
    }
  }

  const previewBulkCollectionChange = async (enabled: boolean) => {
    const scopeSelection = allTagsSelection
    const nodeIds = enabled
      ? selectedHistoryActions.enableNodeIds
      : selectedHistoryActions.disableNodeIds
    if (
      !canConfigureHistory ||
      setTagsEnabledState.isLoading ||
      (scopeSelection === null && nodeIds.length === 0)
    ) {
      return
    }
    setActionError(null)
    try {
      const preview = await setTagsEnabled({
        ...(scopeSelection?.containerScope
          ? { containerScope: scopeSelection.containerScope }
          : {}),
        ...(scopeSelection?.searchQuery ? { searchQuery: scopeSelection.searchQuery } : {}),
        ...(scopeSelection === null ? { nodeIds } : {}),
        enabled,
        preview: true
      })
      setPendingBulkCollection({ enabled, preview })
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error))
    }
  }

  const applyBulkCollectionChange = async () => {
    if (!canConfigureHistory || !pendingBulkCollection || setTagsEnabledState.isLoading) return
    setActionError(null)
    try {
      await setTagsEnabled({
        nodeIds: pendingBulkCollection.preview.resolvedNodeIds,
        enabled: pendingBulkCollection.enabled,
        preview: false
      })
      setPendingBulkCollection(null)
      clearSelectedRows()
      refetchLevel()
      selectedStatusQuery.refetch()
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error))
    }
  }

  const previewBulkDelete = async () => {
    const scopeSelection = allTagsSelection
    if (
      !canDeleteTags ||
      deleteTagsState.isLoading ||
      (scopeSelection === null && selectedRowNodeIds.length === 0)
    ) {
      return
    }
    setActionError(null)
    try {
      const preview = await deleteTags({
        ...(scopeSelection?.containerScope
          ? { containerScope: scopeSelection.containerScope }
          : {}),
        ...(scopeSelection?.searchQuery ? { searchQuery: scopeSelection.searchQuery } : {}),
        ...(scopeSelection === null ? { nodeIds: selectedRowNodeIds } : {}),
        preview: true
      })
      setPendingBulkDelete(preview)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error))
    }
  }

  const applyBulkDelete = async () => {
    if (!canDeleteTags || !pendingBulkDelete || deleteTagsState.isLoading) return
    setActionError(null)
    try {
      await deleteTags({ nodeIds: pendingBulkDelete.resolvedNodeIds, preview: false })
      setPendingBulkDelete(null)
      setSelectedTagKey(null)
      clearSelectedRows()
      refetchLevel()
      selectedStatusQuery.refetch()
    } catch (error) {
      setPendingBulkDelete(null)
      setActionError(error instanceof Error ? error.message : String(error))
    }
  }

  const handleRegistered = useCallback(() => {
    clearSelectedRows()
    if (usesDefaultTreeInventory) treeQuery.refetch()
    else filteredQuery.refetch()
    searchQuery.refetch()
    issuesQuery.refetch()
    selectedStatusQuery.refetch()
    if (liveReadNodeIds.length > 0) liveValuesQuery.refetch()
  }, [
    clearSelectedRows,
    filteredQuery,
    issuesQuery,
    liveReadNodeIds.length,
    liveValuesQuery,
    searchQuery,
    selectedStatusQuery,
    treeQuery,
    usesDefaultTreeInventory
  ])

  const handleSourceChanged = useCallback(() => {
    clearSelectedRows()
    refetchLevel()
    selectedStatusQuery.refetch()
  }, [clearSelectedRows, refetchLevel, selectedStatusQuery])

  const visibleSelection = () => new Set(visibleSelectableNodeIds)

  const setRowSelected = (nodeId: string, checked: boolean) => {
    selectionAnchorNodeIdRef.current = nodeId
    if (allTagsSelection) {
      const next = visibleSelection()
      if (checked) next.add(nodeId)
      else next.delete(nodeId)
      setAllTagsSelection(null)
      setSelectedNodeIds(next)
      return
    }
    setSelectedNodeIds((previous) => {
      const next = new Set(previous)
      if (checked) next.add(nodeId)
      else next.delete(nodeId)
      return next
    })
  }

  const setRowRangeSelected = (nodeId: string, checked: boolean) => {
    const anchorIndex = selectionAnchorNodeIdRef.current
      ? visibleSelectableNodeIds.indexOf(selectionAnchorNodeIdRef.current)
      : -1
    const targetIndex = visibleSelectableNodeIds.indexOf(nodeId)
    if (anchorIndex === -1 || targetIndex === -1) {
      setRowSelected(nodeId, checked)
      return
    }
    const start = Math.min(anchorIndex, targetIndex)
    const end = Math.max(anchorIndex, targetIndex)
    const range = visibleSelectableNodeIds.slice(start, end + 1)
    if (allTagsSelection) {
      const next = visibleSelection()
      for (const rangeNodeId of range) {
        if (checked) next.add(rangeNodeId)
        else next.delete(rangeNodeId)
      }
      setAllTagsSelection(null)
      setSelectedNodeIds(next)
      return
    }
    setSelectedNodeIds((previous) => {
      const next = new Set(previous)
      for (const rangeNodeId of range) {
        if (checked) next.add(rangeNodeId)
        else next.delete(rangeNodeId)
      }
      return next
    })
  }

  const toggleAllVisibleRows = () => {
    if (allTagsSelection) {
      clearSelectedRows()
      return
    }
    setSelectedNodeIds((previous) => {
      const next = new Set(previous)
      for (const nodeId of visibleSelectableNodeIds) {
        if (allVisibleRowsSelected) next.delete(nodeId)
        else next.add(nodeId)
      }
      return next
    })
    selectionAnchorNodeIdRef.current = allVisibleRowsSelected
      ? null
      : (visibleSelectableNodeIds[0] ?? null)
  }

  const toggleAllTags = () => {
    if (!allTagsTarget) return
    if (allTagsSelection) {
      clearSelectedRows()
      return
    }
    selectionAnchorNodeIdRef.current = null
    setSelectedNodeIds(new Set())
    setAllTagsSelection(allTagsTarget)
  }

  // A scope or filter change keeps the previous response around until the new
  // one lands. That stale response must not be shown as the new level, so the
  // list and sidebar stay in their loading state until data for the current
  // request arrives.
  const treeLoadingScope =
    usesDefaultTreeInventory &&
    treeQuery.isLoading &&
    (treeQuery.data === undefined || treeQuery.isPreviousData)
  const filteredLoadingScope =
    filteredQuery.isLoading && (filteredQuery.data === undefined || filteredQuery.isPreviousData)
  const listLoading =
    view === 'search'
      ? !searchIsSettled || (normalizedDebouncedSearch !== '' && searchQuery.isLoading)
      : view === 'issues'
        ? issuesQuery.isLoading
        : usesDefaultTreeInventory
          ? treeLoadingScope
          : filteredLoadingScope
  const listError =
    view === 'search'
      ? searchIsSettled
        ? searchQuery.error
        : undefined
      : view === 'issues'
        ? issuesQuery.error
        : usesDefaultTreeInventory
          ? treeQuery.error
          : filteredQuery.error
  const filterNotComputed =
    view === 'tree' &&
    !usesDefaultTreeInventory &&
    filteredQuery.data?.filterStatus === 'not_computed'
  const sidebarBreadcrumbs =
    view === 'issues'
      ? [
          { id: ROOT_SCOPE.id, label: ROOT_SCOPE.label },
          { id: 'registration-issues', label: 'Registration issues' }
        ]
      : crumbs.map(({ id, label }) => ({ id, label }))
  const previousPage = () => {
    if (view === 'search') {
      setSearchPagination((previous) => {
        const tokens = previous.query === normalizedDebouncedSearch ? previous.tokens : ['']
        return {
          query: normalizedDebouncedSearch,
          tokens: tokens.length > 1 ? tokens.slice(0, -1) : ['']
        }
      })
    } else if (view === 'issues') {
      setIssuesPageTokens((previous) => previous.slice(0, -1))
    } else setPageTokens((previous) => previous.slice(0, -1))
  }
  const nextPage = () => {
    if (!nextPageToken) return
    if (view === 'search') {
      setSearchPagination((previous) => ({
        query: normalizedDebouncedSearch,
        tokens: [
          ...(previous.query === normalizedDebouncedSearch ? previous.tokens : ['']),
          nextPageToken
        ]
      }))
    } else if (view === 'issues') {
      setIssuesPageTokens((previous) => [...previous, nextPageToken])
    } else setPageTokens((previous) => [...previous, nextPageToken])
  }
  return (
    <div className="flex h-full min-h-0 flex-col [contain:size]">
      {liveValuesQuery.isError || liveReadings.transportFailedPolls > LIVE_FAILURE_GRACE_POLLS ? (
        <div className="mx-4 mt-3 rounded-md bg-st-warn-bg p-2.5 text-12 text-st-warn">
          Live tag values are temporarily unavailable.
        </div>
      ) : null}
      {actionError ? (
        <div className="mx-4 mt-3 rounded-md bg-st-down-bg p-2.5 text-12 text-st-down">
          {actionError}
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1">
        <TagTreeSidebar
          parent={sidebarParent}
          folders={sidebarFolders}
          breadcrumbs={sidebarBreadcrumbs}
          countUnavailableIds={sidebarCountUnavailableIds}
          parentCountUnavailable={sidebarParentCountUnavailable}
          isLoading={
            view === 'tree' && (usesDefaultTreeInventory ? treeLoadingScope : filteredLoadingScope)
          }
          filtersActive={view === 'tree' && filtersActive}
          onNavigate={navigateTo}
          onSelectAll={view === 'tree' && !filtersActive ? selectAllInFolder : undefined}
          onNavigateBreadcrumb={navigateBreadcrumb}
          activeView={view}
          searchText={searchText}
          onSearch={() => setView('search')}
          onSearchTextChange={(value) => {
            setView('search')
            setSearchText(value)
            clearSelectedRows()
            setSelectedTagKey(null)
          }}
          onRegistrationIssues={() => setView('issues')}
          registrationIssuesCount={issuesTotalSize}
          onOpenTools={canUseTools ? () => setBundleTool('home') : undefined}
        />

        <ResizablePanelGroup
          orientation="horizontal"
          className="min-w-0 flex-1"
          key={selectedTag ? 'list-detail' : 'list-only'}
        >
          <ResizablePanel defaultSize={selectedTag ? '45%' : '100%'} minSize="16.25rem">
            <div className="flex h-full min-w-0 flex-col">
              <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-1.5">
                <div className="min-w-0">
                  <p className="m-0 truncate font-mono text-11 font-semibold text-ink">
                    {view === 'search'
                      ? 'Search all tags'
                      : view === 'issues'
                        ? 'Registration issues'
                        : tree?.parent.kind === 'providers'
                          ? 'Choose a provider'
                          : tree?.parent.name || scope.label}
                  </p>
                  {view === 'tree' &&
                  tree?.parent.kind === 'provider' &&
                  tree.parent.folderless &&
                  tree.folders.length === 0 ? (
                    <p className="m-0 text-10 text-ink-3">Folderless provider · flat tag list</p>
                  ) : null}
                </div>
                {view === 'issues' && issuesTotalSize !== null ? (
                  <span className="font-mono text-11 text-ink-3">
                    {issuesTotalSize.toLocaleString()} total
                  </span>
                ) : null}
                <div className="flex-1" />
                {view === 'issues' ? (
                  <Select
                    value={issuesStatus}
                    onValueChange={(value) => {
                      if (
                        value === 'all' ||
                        value === 'missing_registration' ||
                        value === 'missing_graph'
                      ) {
                        setIssuesStatus(value)
                        setIssuesPageTokens([''])
                        clearSelectedRows()
                        setSelectedTagKey(null)
                      }
                    }}
                  >
                    <SelectTrigger
                      size="sm"
                      className="h-7 w-45 text-xs"
                      aria-label="Registration issue status"
                      data-testid="registration-issues-filter"
                    >
                      <SelectValue>
                        {issuesStatus === 'all'
                          ? 'All'
                          : issuesStatus === 'missing_registration'
                            ? 'Missing registration'
                            : 'Missing graph tag'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">
                        All
                      </SelectItem>
                      <SelectItem
                        value="missing_registration"
                        className="text-xs"
                        data-testid="registration-issues-filter-missing-registration"
                      >
                        Missing registration
                      </SelectItem>
                      <SelectItem
                        value="missing_graph"
                        className="text-xs"
                        data-testid="registration-issues-filter-missing-graph"
                      >
                        Missing graph tag
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : null}
                {view === 'tree' ? (
                  <>
                    <Select
                      value={acquisitionFilter}
                      onValueChange={(value) => {
                        if (value === 'all' || value === 'enabled' || value === 'disabled') {
                          setAcquisitionFilter(value)
                          setPageTokens([''])
                          clearSelectedRows()
                        }
                      }}
                    >
                      <SelectTrigger
                        size="sm"
                        className="h-7 w-37.5 text-xs"
                        aria-label="History filter"
                        data-testid="tags-acquisition-filter"
                      >
                        <SelectValue>
                          History: {ACQUISITION_FILTER_LABEL[acquisitionFilter]}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="text-xs">
                          All
                        </SelectItem>
                        <SelectItem
                          value="enabled"
                          className="text-xs"
                          data-testid="tags-acquisition-filter-enabled"
                        >
                          Enabled
                        </SelectItem>
                        <SelectItem
                          value="disabled"
                          className="text-xs"
                          data-testid="tags-acquisition-filter-disabled"
                        >
                          Disabled
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={historySourceFilter}
                      onValueChange={(value) => {
                        if (value === 'all' || value === 'webdev' || value === 'opc') {
                          setHistorySourceFilter(value)
                          setPageTokens([''])
                          clearSelectedRows()
                        }
                      }}
                    >
                      <SelectTrigger
                        size="sm"
                        className="h-7 w-45 text-xs"
                        aria-label="History source filter"
                        data-testid="tags-history-source-filter"
                      >
                        <SelectValue>
                          History source: {HISTORY_SOURCE_FILTER_LABEL[historySourceFilter]}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="text-xs">
                          All
                        </SelectItem>
                        <SelectItem
                          value="webdev"
                          className="text-xs"
                          data-testid="tags-history-source-filter-webdev"
                        >
                          WebDev
                        </SelectItem>
                        <SelectItem
                          value="opc"
                          className="text-xs"
                          data-testid="tags-history-source-filter-opc"
                        >
                          OPC
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={activeTab}
                      onValueChange={(value) => {
                        if (
                          value === 'all' ||
                          value === 'needs_registration' ||
                          value === 'problems' ||
                          value === 'warnings' ||
                          value === 'archived'
                        ) {
                          setActiveTab(value)
                          if (value === 'needs_registration') {
                            setAcquisitionFilter('all')
                            setHistorySourceFilter('all')
                          }
                          setPageTokens([''])
                          clearSelectedRows()
                        }
                      }}
                    >
                      <SelectTrigger
                        size="sm"
                        className="h-7 w-37.5 text-xs"
                        aria-label="View filter"
                        data-testid="tags-view-filter"
                      >
                        <SelectValue>View: {TAGS_VIEW_FILTER_LABEL[activeTab]}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="text-xs">
                          All
                        </SelectItem>
                        <SelectItem
                          value="needs_registration"
                          className="text-xs"
                          data-testid="tags-view-filter-needs-registration"
                        >
                          Needs registration
                        </SelectItem>
                        <SelectItem
                          value="problems"
                          className="text-xs"
                          disabled={pageHealthUnavailable}
                          data-testid="tags-health-filter-problems"
                        >
                          Problems
                        </SelectItem>
                        <SelectItem
                          value="warnings"
                          className="text-xs"
                          disabled={pageHealthUnavailable}
                          data-testid="tags-health-filter-warnings"
                        >
                          Warnings
                        </SelectItem>
                        <SelectItem
                          value="archived"
                          className="text-xs"
                          data-testid="tags-view-filter-archived"
                        >
                          Archived
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : null}
                {visibleSelectableNodeIds.length > 0 && allTagsSelection === null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2.5 text-11"
                    onClick={toggleAllVisibleRows}
                    title="Select all visible tags on this page"
                    data-testid="tag-select-all"
                  >
                    {allVisibleRowsSelected ? 'Clear all' : 'Select all'}
                  </Button>
                ) : null}
                {allTagsTarget ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2.5 text-11"
                    onClick={toggleAllTags}
                    title={
                      allTagsSelection
                        ? 'Clear the selection of every tag in this scope'
                        : 'Select every tag in this scope, including other pages and nested folders'
                    }
                    data-testid="tag-select-all-tags"
                  >
                    {allTagsSelection ? 'Clear all tags' : 'Select all tags'}
                  </Button>
                ) : null}
                {view !== 'issues' && healthQuery.isLoading ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-11 text-ink-3"
                    data-testid="tags-health-assessing"
                  >
                    <Loader2 className="size-3 animate-spin" aria-hidden />
                    assessing…
                  </span>
                ) : healthQuery.error ? (
                  <span className="text-11 text-ink-3" data-testid="tags-health-unavailable">
                    Trend data unavailable
                  </span>
                ) : null}
              </div>

              {selectedNodeIds.size > 0 || allTagsSelection ? (
                <div
                  className="flex items-center gap-3 border-b border-brand/20 bg-brand-soft px-4 py-2"
                  data-testid="tag-selection-bar"
                  role="status"
                  aria-live="polite"
                >
                  <span className="text-xs font-semibold text-ink">
                    {allTagsSelection
                      ? allTagsSelection.summary
                      : `${selectedNodeIds.size} selected in this view`}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-11"
                    onClick={clearSelectedRows}
                  >
                    Clear
                  </Button>
                  <div
                    className="flex flex-1 items-center justify-end gap-1.5"
                    data-testid="tag-selection-actions"
                  >
                    {canWriteTags &&
                    (allTagsSelection !== null || selectedGraphOnlyNodeIds.length > 0) ? (
                      <Button
                        size="sm"
                        className="h-7 px-2.5 text-11"
                        onClick={() =>
                          setRegisterSelection(
                            allTagsSelection
                              ? {
                                  nodeIds: [],
                                  containerScope: allTagsSelection.containerScope,
                                  searchQuery: allTagsSelection.searchQuery
                                }
                              : { nodeIds: selectedGraphOnlyNodeIds }
                          )
                        }
                        data-testid="tag-selection-register"
                      >
                        Register history
                      </Button>
                    ) : null}
                    {allTagsSelection !== null || selectedRegisteredNodeIds.length > 0 ? (
                      <>
                        {canManageAcquisition && selectedChangeSourceEligibility.visible ? (
                          <span title={selectedChangeSourceEligibility.reason}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2.5 text-11"
                              disabled={!selectedChangeSourceEligibility.enabled}
                              onClick={() => setChangeSourceTags([...selectedRegisteredRows])}
                              data-testid="tag-selection-change-source"
                              data-disabled-reason={selectedChangeSourceEligibility.reason}
                            >
                              Change source
                            </Button>
                          </span>
                        ) : null}
                        {canConfigureHistory &&
                        (allTagsSelection !== null ||
                          selectedHistoryActions.enableNodeIds.length > 0) ? (
                          <Button
                            size="sm"
                            className="h-7 px-2.5 text-11"
                            disabled={setTagsEnabledState.isLoading}
                            onClick={() => void previewBulkCollectionChange(true)}
                            data-testid="tag-selection-resume"
                          >
                            Enable history
                          </Button>
                        ) : null}
                        {canConfigureHistory &&
                        (allTagsSelection !== null ||
                          selectedHistoryActions.disableNodeIds.length > 0) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-11 text-st-down"
                            disabled={setTagsEnabledState.isLoading}
                            onClick={() => void previewBulkCollectionChange(false)}
                            data-testid="tag-selection-stop"
                          >
                            Disable history
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    {canDeleteTags &&
                    (allTagsSelection !== null || selectedRowNodeIds.length > 0) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2.5 text-11 text-st-down"
                        disabled={deleteTagsState.isLoading}
                        onClick={() => void previewBulkDelete()}
                        data-testid="tag-selection-delete"
                      >
                        <Trash2 className="size-3" aria-hidden />
                        Delete tags
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div ref={listRef} className="min-h-0 flex-1 overflow-auto">
                {listLoading ? (
                  <div className="flex items-center gap-2 p-6 text-sm text-ink-3">
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    {view === 'search'
                      ? 'Searching tags…'
                      : view === 'issues'
                        ? 'Loading registration issues…'
                        : 'Loading this level…'}
                  </div>
                ) : listError ? (
                  <div className="flex flex-col items-center gap-3 p-12">
                    <p className="m-0 text-center text-sm text-st-down">
                      Failed to load {view === 'tree' ? 'this inventory level' : view}: {listError}
                    </p>
                    <Button size="sm" variant="outline" onClick={refetchLevel}>
                      Retry
                    </Button>
                  </div>
                ) : filterNotComputed ? (
                  <div
                    className="flex flex-col items-center gap-2 p-12 text-center"
                    data-testid="tags-filter-not-computed"
                  >
                    <p className="m-0 text-sm font-medium text-ink">
                      Exact filter results are unavailable for this scope.
                    </p>
                    <p className="m-0 max-w-md text-xs text-ink-3">
                      {filteredQuery.data?.filterTagCountAtLeast
                        ? `This scope requires evaluating at least ${filteredQuery.data.filterTagCountAtLeast.toLocaleString()} tags. `
                        : ''}
                      Narrow the inventory scope to evaluate this filter.
                    </p>
                  </div>
                ) : visibleTags.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 p-12">
                    <p className="m-0 text-center text-sm text-ink-3">
                      {view === 'search' && normalizedDebouncedSearch === ''
                        ? 'Search by tag name or path across every provider.'
                        : view === 'search'
                          ? 'No matching ontology tags.'
                          : view === 'issues'
                            ? issuesStatus === 'missing_registration'
                              ? 'Every ontology tag is registered.'
                              : issuesStatus === 'missing_graph'
                                ? 'Every registration is linked to an ontology tag.'
                                : 'No registration issues.'
                            : tree?.parent.kind === 'providers'
                              ? 'Choose a provider from the inventory tree.'
                              : acquisitionFilter === 'enabled' && scopeHasTags
                                ? 'No history-enabled tags on this page.'
                                : acquisitionFilter === 'disabled' && scopeHasTags
                                  ? 'No history-disabled tags on this page.'
                                  : activeTab === 'needs_registration' && scopeHasTags
                                    ? 'Every tag on this page is registered.'
                                    : activeTab === 'problems' && scopeHasTags
                                      ? 'No problem tags on this page.'
                                      : activeTab === 'warnings' && scopeHasTags
                                        ? 'No warning tags on this page.'
                                        : activeTab === 'archived' && scopeHasTags
                                          ? 'No archived tags on this page.'
                                          : sidebarFolders.length
                                            ? 'This level contains containers but no direct tags.'
                                            : 'No tags on this level.'}
                    </p>
                  </div>
                ) : view === 'issues' ? (
                  <div data-testid="registration-issues-list" className="flex flex-col">
                    <div className="sticky top-0 z-10 grid grid-cols-[1.25rem_1fr_1fr_8rem_4.875rem] gap-3 border-b border-line bg-bg-elev px-4 py-1 text-9 font-semibold uppercase tracking-wide text-ink-3">
                      <span />
                      <span>Tag</span>
                      <span>Source</span>
                      <span>Status</span>
                      <span />
                    </div>
                    {visibleTags.map((tag) => {
                      const nodeId = rowGraphNodeID(tag)
                      const missingRegistration =
                        tag.mismatchStatus === 'missing_registration' && nodeId !== undefined
                      const selected = nodeIsSelected(nodeId)
                      const focused = rowIdentityKey(tag) === selectedTagKey
                      return (
                        <div
                          key={rowIdentityKey(tag)}
                          data-row-key={rowIdentityKey(tag)}
                          className={cn(
                            'grid grid-cols-[1.25rem_1fr_1fr_8rem_4.875rem] items-center gap-3 border-b border-line border-l-[3px] px-4 py-2',
                            focused
                              ? 'border-l-brand bg-brand-soft'
                              : 'border-l-transparent hover:bg-bg-sunk/60'
                          )}
                          data-testid={`registration-issue-${tag.id}`}
                        >
                          {missingRegistration && nodeId && canWriteTags ? (
                            <Checkbox
                              checked={selected}
                              onClick={(event) => {
                                if (event.shiftKey) {
                                  setRowRangeSelected(nodeId, !selected)
                                } else {
                                  setRowSelected(nodeId, !selected)
                                }
                              }}
                              aria-label={`Select ${rowDisplayText(tag)}`}
                              data-testid={`tag-select-${tag.id}`}
                            />
                          ) : (
                            <span aria-hidden />
                          )}
                          <button
                            type="button"
                            className="min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => setSelectedTagKey(rowIdentityKey(tag))}
                          >
                            <div
                              className="truncate font-mono text-12 text-ink"
                              title={tag.tagPath}
                            >
                              {tag.displayLabel || tag.tagPath}
                            </div>
                            {tag.displayLabel ? (
                              <div className="truncate font-mono text-10 text-ink-3">
                                {tag.tagPath}
                              </div>
                            ) : null}
                          </button>
                          <div className="min-w-0">
                            <div
                              className="truncate text-11 text-ink-2"
                              title={tag.sourceUrl || tag.provider}
                            >
                              {missingRegistration
                                ? tag.provider || 'Unknown provider'
                                : tag.sourceUrl || 'No source URL'}
                            </div>
                            <div className="text-10 uppercase text-ink-3">
                              {missingRegistration
                                ? TAG_VENDOR_LABEL[rowVendor(tag)]
                                : tag.sourceType}
                            </div>
                          </div>
                          <span
                            className={cn(
                              'justify-self-start rounded px-2 py-0.5 text-10 font-semibold',
                              missingRegistration
                                ? 'bg-st-warn-bg text-st-warn'
                                : 'bg-st-down-bg text-st-down'
                            )}
                            data-testid={`registration-issue-status-${tag.id}`}
                          >
                            {missingRegistration ? 'Missing registration' : 'Missing graph tag'}
                          </span>
                          {missingRegistration && nodeId ? (
                            <Button
                              size="sm"
                              className="h-6 px-2 text-10"
                              onClick={() => setRegisterSelection({ nodeIds: [nodeId] })}
                            >
                              Register
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-10"
                              onClick={() => setSelectedTagKey(rowIdentityKey(tag))}
                            >
                              Review
                            </Button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div data-testid="tags-middle-list" className="flex flex-col">
                    <div
                      className={cn(
                        'sticky top-0 z-10 grid items-center gap-2 border-b border-line bg-bg-elev px-3 py-1 text-9 font-semibold uppercase tracking-wide text-ink-3',
                        selectedTag
                          ? 'grid-cols-[1.25rem_1fr_6.5rem]'
                          : 'grid-cols-[1.25rem_1fr_6.5rem_4rem_5.625rem_5rem]'
                      )}
                      data-testid="tags-list-header"
                      aria-hidden
                    >
                      <span />
                      <span>Tag</span>
                      <span>Trend ({INVENTORY_TREND_WINDOW_LABEL})</span>
                      {selectedTag ? null : (
                        <>
                          <span>Live</span>
                          <span className="text-right">Value</span>
                          <span className="text-right">Updated</span>
                        </>
                      )}
                    </div>
                    {visibleTags.map((tag) => {
                      const nodeId = rowGraphNodeID(tag)
                      if (!nodeId) return null
                      const {
                        reading: liveReading,
                        connectivity,
                        failure: liveFailure
                      } = liveRowState(liveReadings, nodeId)
                      const focused = rowIdentityKey(tag) === selectedTagKey
                      const selected = nodeIsSelected(nodeId)
                      const serverHealth = healthById.get(nodeId)
                      const awaitingServerHealth =
                        healthRequestIds.includes(nodeId) && healthQuery.isLoading && !serverHealth
                      // A non-historized tag never shows a computed trend,
                      // whatever the server evaluated from older readings.
                      const rowHealth: RowHealthStatus = !isTagTrendEligible(tag)
                        ? 'unknown'
                        : healthQuery.error
                          ? 'unavailable'
                          : awaitingServerHealth
                            ? 'pending'
                            : (serverHealth?.status ?? 'unknown')
                      const historySourceLabel = rowHistorySourceLabel(tag)
                      const historization = rowHistorization(tag)
                      const compact = Boolean(selectedTag)
                      return (
                        <div
                          key={nodeId}
                          data-row-key={rowIdentityKey(tag)}
                          data-testid={`tag-row-${tag.id}`}
                          className={cn(
                            'grid w-full items-center gap-2 border-b border-line border-l-[3px] px-3 py-1.5 transition-colors',
                            compact
                              ? 'grid-cols-[1.25rem_1fr_6.5rem]'
                              : 'grid-cols-[1.25rem_1fr_6.5rem_4rem_5.625rem_5rem]',
                            focused
                              ? 'border-l-brand bg-brand-soft'
                              : 'border-l-transparent hover:bg-bg-sunk/60'
                          )}
                        >
                          <Checkbox
                            checked={selected}
                            onClick={(event) => {
                              if (event.shiftKey) {
                                setRowRangeSelected(nodeId, !selected)
                              } else {
                                setRowSelected(nodeId, !selected)
                              }
                            }}
                            aria-label={`Select ${rowDisplayText(tag)}`}
                            data-testid={`tag-select-${tag.id}`}
                          />
                          <button
                            type="button"
                            onClick={(event) => {
                              if (event.shiftKey) {
                                setRowRangeSelected(nodeId, true)
                                return
                              }
                              if (event.metaKey || event.ctrlKey) {
                                setRowSelected(nodeId, !selected)
                                return
                              }
                              setSelectedTagKey(rowIdentityKey(tag))
                            }}
                            aria-pressed={focused}
                            className="min-w-0 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <div className="flex min-w-0 items-center gap-1.5">
                              <span
                                className="truncate font-mono text-13 text-ink"
                                title={rowDisplayText(tag)}
                              >
                                {rowDisplayText(tag)}
                              </span>
                              {historySourceLabel ? (
                                <span
                                  className="shrink-0 rounded bg-bg-sunk px-1.5 py-0.5 text-10 font-semibold text-ink-3"
                                  data-testid={`tag-history-source-${tag.id}`}
                                  title={`${historization.label}: ${historization.reason}`}
                                >
                                  {historySourceLabel}
                                  {tag.piHistory && historySourceLabel !== 'PI'
                                    ? ' · PI'
                                    : tag.canaryHistory && historySourceLabel !== 'Canary'
                                      ? ' · Canary'
                                      : ''}
                                </span>
                              ) : null}
                              {tag.graphOnly ? (
                                <span className="shrink-0 rounded bg-st-warn-bg px-1.5 py-0.5 text-9 font-semibold text-st-warn">
                                  Needs registration
                                </span>
                              ) : null}
                            </div>
                            {rowGraphPath(tag) !== tag.tagPath ? (
                              <div className="truncate font-mono text-11 text-ink-3">
                                {tag.tagPath}
                              </div>
                            ) : null}
                          </button>
                          <HealthChip
                            status={rowHealth}
                            reason={serverHealth?.reason}
                            notCollectedReason={rowNotCollectedReason(tag)}
                            tagId={tag.id}
                          />
                          {compact ? null : (
                            <>
                              <span
                                className={cn(
                                  'inline-flex items-center justify-self-start rounded-full px-2 py-0.5 text-11 font-semibold',
                                  connectivityPillClass(connectivity)
                                )}
                                title={
                                  liveFailure ??
                                  liveReading?.error ??
                                  liveConnectivityDescription(connectivity)
                                }
                                data-testid={`tag-connectivity-${tag.id}`}
                              >
                                {liveConnectivityLabel(connectivity)}
                              </span>
                              <span
                                className="truncate text-right font-mono text-11 text-ink"
                                title={
                                  liveReading?.error ??
                                  (liveReading?.value === undefined
                                    ? undefined
                                    : stringifyTagValue(liveReading.value))
                                }
                                data-testid={`tag-live-value-${tag.id}`}
                              >
                                {formatLiveValue(liveReading)}
                              </span>
                              <span className="text-right font-mono text-11 text-ink-3">
                                {formatRelativeTime(liveReading?.timestamp, {
                                  fallback: '—'
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {currentPageTokens.length > 1 || nextPageToken ? (
                <div
                  className="flex items-center justify-center gap-2 border-t border-line px-4 py-1.5"
                  data-testid="tags-pagination"
                >
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={currentPageTokens.length === 1}
                    onClick={previousPage}
                  >
                    Previous
                  </Button>
                  <span className="font-mono text-11 text-ink-3">
                    Page {currentPageTokens.length}
                  </span>
                  <Button size="sm" variant="outline" disabled={!nextPageToken} onClick={nextPage}>
                    Next
                  </Button>
                </div>
              ) : null}
            </div>
          </ResizablePanel>

          {selectedTag ? (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize="55%" minSize="26.25rem">
                <TagDetailPanel
                  tag={selectedTag}
                  status={selectedStatusQuery.data}
                  connectivity={selectedLive.connectivity}
                  liveReading={selectedLive.reading}
                  liveFailure={selectedLive.failure}
                  onClose={closeSelectedTag}
                  onDelete={
                    canWriteTags && !selectedTag.graphOnly
                      ? (tag) => setPendingDelete(tag)
                      : undefined
                  }
                  graphTagPath={rowGraphPath(selectedTag)}
                  graphNodeId={selectedGraphNodeId}
                  graphActionsEnabled={selectedGraphNodeId !== undefined}
                  graphOnly={selectedTag.graphOnly}
                  registrationVendor={selectedTag.registrationVendor}
                  registrationProtocol={selectedTag.registrationProtocol}
                  acquisitionQuarantined={selectedTag.acquisitionQuarantined}
                  gatewayHistoryEnabled={selectedTag.gatewayHistoryEnabled}
                  piHistory={selectedTag.piHistory}
                  canaryHistory={selectedTag.canaryHistory}
                  classification={
                    selectedGraphNodeId
                      ? classificationsQuery.data?.get(selectedGraphNodeId)
                      : undefined
                  }
                  onClassificationSaved={
                    selectedGraphNodeId ? classificationsQuery.refetch : undefined
                  }
                  archived={selectedTag.archived ?? false}
                  onToggleArchive={
                    canWriteGraph && selectedGraphNodeId ? handleToggleArchive : undefined
                  }
                  onAcquisitionChanged={() => {
                    refetchLevel()
                    selectedStatusQuery.refetch()
                  }}
                  onRegisterAcquisition={
                    canWriteTags && selectedTag.graphOnly && selectedGraphNodeId
                      ? () => setRegisterSelection({ nodeIds: [selectedGraphNodeId] })
                      : undefined
                  }
                  onChangeSource={
                    canManageAcquisition && !selectedTag.graphOnly
                      ? () => setChangeSourceTags([selectedTag])
                      : undefined
                  }
                  canConfigureHistory={canConfigureHistory}
                  classificationEnabled={canClassifyTags}
                  observedStates={summariesQuery.data
                    .get(tagSummaryIdentityKey(selectedTag.sourceUrl, selectedTag.tagPath))
                    ?.state?.states.map((s) => s.state)}
                />
              </ResizablePanel>
            </>
          ) : null}
        </ResizablePanelGroup>
      </div>
      {debugEnabled ? <HistorianStatusBar /> : null}

      <TagBundleToolsDrawer
        open={activeBundleTool !== null}
        tool={activeBundleTool ?? 'home'}
        onOpenChange={(open) => {
          if (!open) setBundleTool(null)
        }}
        onToolChange={setBundleTool}
        onImported={handleBundleImported}
        onTagCreated={refetchLevel}
        onTagsCleanedUp={refetchLevel}
        onClassifyTags={() => void handleClassify()}
        classifyDisabled={isClassifying || classifiableTags.length === 0}
        classifyProgress={classifyProgress}
        classifyNotice={classifyNotice}
        classifiableTagCount={classifiableTags.length}
        canWriteTags={canWriteTags}
        canClassifyTags={canRunTagClassification}
        canMaintainBundles={canMaintainBundles}
        showOrphanCleanup={showOrphanCleanup}
        allowReplaceImport={debugEnabled}
      />
      <RegisterTagsDialog
        open={canWriteTags && registerSelection !== null}
        selection={registerSelection ?? { nodeIds: [] }}
        onOpenChange={(open) => {
          if (!open) setRegisterSelection(null)
        }}
        onRegistered={handleRegistered}
      />
      <ChangeSourceDialog
        open={canManageAcquisition && changeSourceTags.length > 0}
        tags={changeSourceTags}
        onOpenChange={(open) => {
          if (!open) setChangeSourceTags([])
        }}
        onApplied={handleSourceChanged}
      />

      <AlertDialog
        open={canWriteTags && Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open && !deleteState.isLoading) setPendingDelete(null)
        }}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong className="font-mono">{pendingDelete?.tagPath ?? ''}</strong>? The proxy will
              stop scraping this tag immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteState.isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                event.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={!canWriteTags || deleteState.isLoading}
            >
              {deleteState.isLoading ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={canDeleteTags && pendingBulkDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteTagsState.isLoading) setPendingBulkDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected tags?</AlertDialogTitle>
            <AlertDialogDescription>
              These tags are removed from the ontology graph on the current branch, and any that
              collect history stop collecting and leave the tag registry. History already recorded
              is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingBulkDelete ? (
            <div
              className="rounded-md border border-line bg-bg-sunk/50 p-3 text-12"
              data-testid="tag-bulk-delete-preview"
            >
              <div className="flex justify-between gap-3">
                <span className="text-ink-3">Tags deleted</span>
                <strong>{pendingBulkDelete.selectedCount.toLocaleString()}</strong>
              </div>
              <div className="mt-1 flex justify-between gap-3">
                <span className="text-ink-3">Stop collecting history</span>
                <strong>{pendingBulkDelete.registeredCount.toLocaleString()}</strong>
              </div>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTagsState.isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                event.preventDefault()
                void applyBulkDelete()
              }}
              disabled={
                deleteTagsState.isLoading ||
                !canDeleteTags ||
                !pendingBulkDelete ||
                pendingBulkDelete.selectedCount === 0
              }
              data-testid="tag-bulk-delete-confirm"
            >
              {deleteTagsState.isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Deleting…
                </>
              ) : (
                `Delete ${pendingBulkDelete?.selectedCount.toLocaleString() ?? ''} tags`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={canConfigureHistory && pendingBulkCollection !== null}
        onOpenChange={(open) => {
          if (!open && !setTagsEnabledState.isLoading) setPendingBulkCollection(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingBulkCollection?.enabled
                ? 'Enable history for selected tags?'
                : 'Disable history for selected tags?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBulkCollection?.enabled
                ? 'Scheduled collection and new history will resume for the registered tags in this snapshot.'
                : 'Scheduled collection and new history will stop for the registered tags in this snapshot. Live reads remain available.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingBulkCollection ? (
            <div
              className="rounded-md border border-line bg-bg-sunk/50 p-3 text-12"
              data-testid="tag-bulk-collection-preview"
            >
              <div className="flex justify-between gap-3">
                <span className="text-ink-3">Registered tags affected</span>
                <strong>{pendingBulkCollection.preview.registeredCount.toLocaleString()}</strong>
              </div>
              <div className="mt-1 flex justify-between gap-3">
                <span className="text-ink-3">Graph-only tags skipped</span>
                <strong>{pendingBulkCollection.preview.graphOnlyCount.toLocaleString()}</strong>
              </div>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setTagsEnabledState.isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={pendingBulkCollection?.enabled ? 'default' : 'destructive'}
              onClick={(event) => {
                event.preventDefault()
                void applyBulkCollectionChange()
              }}
              disabled={
                setTagsEnabledState.isLoading ||
                !canConfigureHistory ||
                !pendingBulkCollection ||
                pendingBulkCollection.preview.registeredCount === 0
              }
              data-testid="tag-bulk-collection-confirm"
            >
              {setTagsEnabledState.isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Updating…
                </>
              ) : pendingBulkCollection?.enabled ? (
                'Enable history'
              ) : (
                'Disable history'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default TagsPage
